import { useState } from "react";
import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import { Banner } from "components/DesignSystem/Banner/Banner";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { DnDArea, File as FileItem } from "components/design/UploadFileSection";
import { FileIcon } from "components/design/FileIcon";
import { Cross } from "components/icons/Cross";
import { useToast } from "hooks/useToast";
import {
  useSetSyncConfigMutation,
  useClearSyncConfigMutation,
  useGetIncomeStatementUploadQuery,
  useDeleteIncomeStatementUploadMutation,
  useUploadIncomeStatementMutation,
} from "store/apis/estimatedTax";
import {
  SyncConfig,
  IncomeStatementUploadResult,
  MonthlyBreakdownEntry,
} from "types/Models/estimatedTax";
import { BackendError } from "types/utils/error";
import { MONTH_OPTIONS } from "./constants";
import { UploadedStatementDetailModal } from "./UploadedStatementDetailModal";
import { Spinner } from "components/DesignSystem/Loader/loader";
import { TrashSimple } from "components/icons/TrashSimple";
import { FilePreviewTag } from "components/FilePreviewTag/FilePreviewTag";
import { FolderUpload } from "components/icons/FolderUpload";
import { Sync } from "components/icons/Sync";
import { pluralize } from "utils/pluralize";

type Props = {
  open: boolean;
  onClose: () => void;
  entityId: string;
  year: string;
  syncConfig: SyncConfig | null;
  months?: MonthlyBreakdownEntry[];
};

type UploadModalState = "upload" | "confirm_overwrite";

export const SyncDataModal = ({
  open,
  onClose,
  entityId,
  year,
  syncConfig,
  months = [],
}: Props) => {
  const { alertToast, successToast } = useToast();

  const existingMonth = syncConfig ? String(syncConfig.sync_up_to_month) : "";
  const [selectedMonth, setSelectedMonth] = useState(existingMonth);
  const [statementDetailOpen, setStatementDetailOpen] = useState(false);

  const [files, setFiles] = useState<File[]>([]);
  const [uploadModalState, setUploadModalState] =
    useState<UploadModalState>("upload");
  const [overwriteWarning, setOverwriteWarning] = useState("");

  const { data: incomeStatementUpload, isLoading: isLoadingUpload } =
    useGetIncomeStatementUploadQuery(
      { entityId, taxYear: year },
      { skip: !open }
    );
  const hasUploadedStatement =
    incomeStatementUpload && "uuid" in incomeStatementUpload;

  const [deleteIncomeStatement, { isLoading: isDeletingStatement }] =
    useDeleteIncomeStatementUploadMutation();

  const [setSyncConfig, { isLoading: isSetting }] = useSetSyncConfigMutation();
  const [clearSyncConfig, { isLoading: isClearing }] =
    useClearSyncConfigMutation();
  const [uploadIncomeStatement, { isLoading: isUploading }] =
    useUploadIncomeStatementMutation();

  const closedMonthsCount = months.filter((m) => m.source === "CLOSED").length;

  const handleDeleteStatement = async () => {
    try {
      await deleteIncomeStatement({ entityId, taxYear: year }).unwrap();
      successToast({ message: "Income statement removed" });
    } catch {
      alertToast({ message: "Failed to remove income statement" });
    }
  };

  const handleFetch = async () => {
    if (!selectedMonth) return;
    try {
      await setSyncConfig({
        entityId,
        payload: { year, sync_up_to_month: selectedMonth },
      }).unwrap();
      successToast({ message: "Sync configured successfully" });
      onClose();
    } catch (e) {
      alertToast({
        message:
          (e as BackendError)?.data?.error?.message ||
          "Failed to configure sync",
      });
    }
  };

  const handleClearSync = async () => {
    setSelectedMonth("");
    if (!syncConfig) return;
    try {
      await clearSyncConfig({ entityId, payload: { year } }).unwrap();
      successToast({ message: "Sync cleared" });
    } catch (e) {
      alertToast({
        message:
          (e as BackendError)?.data?.error?.message || "Failed to clear sync",
      });
    }
  };

  const handleDrop = (dropped: File[]) => {
    setFiles(dropped.slice(0, 1));
  };

  const handleRemoveFile = () => {
    setFiles([]);
  };

  const buildFormData = (confirmOverwrite?: boolean) => {
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    formData.append("tax_year", year);
    if (confirmOverwrite) formData.append("confirm_overwrite", "true");
    return formData;
  };

  const handleUpload = async (confirmOverwrite = false) => {
    if (files.length === 0) return;
    try {
      await uploadIncomeStatement({
        entityId,
        formData: buildFormData(confirmOverwrite),
      }).unwrap();
      successToast({ message: `Income statement synced for ${year}` });
      resetUploadState();
    } catch (e: unknown) {
      const err = e as {
        status?: number;
        data?: { warning?: string; error?: string };
      };

      if (err.status === 409 && err.data?.warning) {
        setOverwriteWarning(err.data.warning);
        setUploadModalState("confirm_overwrite");
        return;
      }

      alertToast({
        message: err.data?.error || "Failed to extract data from the file",
      });
      setFiles([]);
    }
  };

  const resetUploadState = () => {
    setFiles([]);
    setUploadModalState("upload");
    setOverwriteWarning("");
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedMonth(existingMonth);
      resetUploadState();
      onClose();
    }
  };

  const handleMonthChange = (option: OptionData | null) => {
    if (!option) {
      void handleClearSync();
      return;
    }
    setSelectedMonth(option.value);
  };

  return (
    <>
      <Modal.Root open={open} onOpenChange={handleOpenChange}>
        <Modal.Content useCustomOverlay>
          <Modal.Header className="!t-border-b-neutral-10 !t-py-4 !t-pl-8 !t-pr-4">
            <Modal.Title className="!t-text-title-h2-bold">
              Update Financials
            </Modal.Title>
            <Modal.Close onClick={onClose} />
          </Modal.Header>

          {isLoadingUpload ? (
            <Modal.Body>
              <div className="t-flex t-justify-center t-py-8">
                <Spinner size="small" />
              </div>
            </Modal.Body>
          ) : uploadModalState === "confirm_overwrite" ? (
            <>
              <Modal.Body>
                <div className="t-rounded-lg t-bg-orange-0 t-border t-border-orange-20 t-p-4 t-text-body-sm t-text-text-60">
                  <p className="t-m-0">
                    {overwriteWarning ||
                      `Uploading a new statement for ${year} will overwrite previously uploaded financials for that year. Do you want to continue?`}
                  </p>
                </div>
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Button disabled={isUploading} onClick={resetUploadState}>
                  Cancel
                </Button>
                <Button
                  customType="danger"
                  isLoading={isUploading}
                  disabled={isUploading}
                  onClick={() => handleUpload(true)}
                >
                  Yes, Overwrite
                </Button>
              </Modal.FooterButtonGroup>
            </>
          ) : (
            <Modal.Body className="!t-pt-8">
              <div>
                <div>
                  <div className="t-flex t-flex-wrap t-items-center t-gap-1.5">
                    <span className="t-text-subtitle-lg t-text-text-100">
                      Fetch from Books
                    </span>
                    <span className="t-shrink-0 t-rounded-full t-bg-purple-0 t-px-2 t-py-0.5 t-text-body t-text-purple">
                      Recommended
                    </span>
                  </div>
                  <p className="t-mb-0 t-mt-1.5 t-text-body t-text-text-30">
                    Sync income statement figures from Inkle Books for all
                    months up to your selected cutoff.
                  </p>
                </div>
                <div className="t-mt-4">
                  <Combobox
                    size="regular"
                    label="Sync up to month"
                    options={MONTH_OPTIONS}
                    value={
                      MONTH_OPTIONS.find((o) => o.value === selectedMonth) ||
                      null
                    }
                    onChange={(option) =>
                      handleMonthChange(option as OptionData | null)
                    }
                    isClearable={Boolean(selectedMonth)}
                    placeholder="Select month"
                    menuPortalTarget={document.body}
                  />
                </div>
                <Button
                  customType="primary"
                  size="regular"
                  isLoading={isSetting}
                  disabled={isSetting || isClearing || !selectedMonth}
                  onClick={handleFetch}
                  block
                  className="!t-mt-3"
                >
                  <div className="t-flex t-items-center t-justify-center t-gap-2 !t-text-body">
                    <Sync size="20" />
                    Fetch latest financials
                  </div>
                </Button>
              </div>

              <div className="t-mt-5 t-flex t-items-center t-gap-2">
                <span className="t-h-px t-flex-1 t-bg-neutral-10" />
                <span className="t-text-body t-leading-none t-text-text-30">
                  or
                </span>
                <span className="t-h-px t-flex-1 t-bg-neutral-10" />
              </div>

              <div className="t-mt-5">
                <span className="t-text-subtitle-lg t-text-text-100">
                  Upload manually
                </span>
                <p className="t-mb-0 t-mt-1.5 t-text-body t-text-text-30">
                  Upload your latest income statement as a PDF, CSV, or XLSX
                  file.
                </p>
              </div>

              <div className="t-mt-4 t-space-y-2">
                {hasUploadedStatement ? (
                  <div className="t-flex t-items-center t-gap-2 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-bg-surface-lighter-grey t-px-3 t-py-2">
                    <span className="t-text-body-sm t-text-text-30 t-shrink-0">
                      Uploaded file:
                    </span>
                    <div className="t-flex-1 t-min-w-0">
                      {incomeStatementUpload.file ? (
                        <FilePreviewTag
                          file={
                            incomeStatementUpload.file as Parameters<
                              typeof FilePreviewTag
                            >[0]["file"]
                          }
                          size="small"
                          className="!t-border-none !t-bg-transparent !t-p-0 !t-text-purple t-underline hover:!t-bg-transparent [&_div]:!t-max-w-xs"
                        />
                      ) : (
                        <Button
                          customType="link"
                          size="regular"
                          onClick={() => setStatementDetailOpen(true)}
                        >
                          <div className="t-flex t-items-center t-gap-1.5">
                            <FolderUpload />
                            Income Statement
                          </div>
                        </Button>
                      )}
                    </div>
                    <Button
                      customType="ghost_icon"
                      size="regular"
                      isLoading={isDeletingStatement}
                      disabled={isDeletingStatement}
                      onClick={handleDeleteStatement}
                    >
                      <TrashSimple />
                    </Button>
                  </div>
                ) : (
                  <DnDArea
                    isRowLayout
                    onDrop={handleDrop}
                    accept={{
                      "application/pdf": [],
                      "text/csv": [],
                      "image/png": [],
                      "image/jpeg": [],
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                        [],
                      "application/vnd.ms-excel": [],
                    }}
                  />
                )}

                {closedMonthsCount > 0 && (
                  <Banner
                    type="neutral"
                    description={`${pluralize(
                      closedMonthsCount,
                      "month",
                      "months"
                    )} in ${year} ${
                      closedMonthsCount === 1 ? "is" : "are"
                    } already closed and will be skipped. Uploaded figures won't affect closed months.`}
                  />
                )}
                {files.length > 0 && (
                  <div className="t-flex t-flex-col t-gap-2">
                    {files.map((file) =>
                      isUploading ? (
                        <FileItem
                          key={file.name}
                          file={file}
                          isUploading
                          onCancel={() => {}}
                        />
                      ) : (
                        <div
                          key={file.name}
                          className="t-border t-border-solid t-rounded t-border-neutral-20 t-bg-surface-lighter-grey t-p-3 t-flex t-items-center t-gap-3 t-w-full"
                        >
                          <FileIcon fileType={file.type} size="40" />
                          <div className="t-flex t-flex-1 t-flex-col t-gap-1 t-min-w-0">
                            <span className="t-text-blue-100 t-text-subtitle-sm t-truncate">
                              {file.name}
                            </span>
                            <span className="t-text-neutral t-text-caption">
                              {(file.size / 1024).toFixed(2)} KB
                            </span>
                          </div>
                          <Button
                            customType="ghost_icon"
                            size="xsmall"
                            onClick={handleRemoveFile}
                          >
                            <Cross />
                          </Button>
                        </div>
                      )
                    )}
                  </div>
                )}

                <Button
                  customType="secondary"
                  isLoading={isUploading}
                  disabled={files.length === 0 || isUploading}
                  onClick={() => handleUpload(false)}
                  block
                >
                  Upload & Sync
                </Button>
              </div>
            </Modal.Body>
          )}
        </Modal.Content>
      </Modal.Root>

      {hasUploadedStatement && statementDetailOpen && (
        <UploadedStatementDetailModal
          open={statementDetailOpen}
          onClose={() => setStatementDetailOpen(false)}
          upload={incomeStatementUpload as IncomeStatementUploadResult}
          onReupload={() => setStatementDetailOpen(false)}
        />
      )}
    </>
  );
};
